import { RouteItem, expected } from '@aloft/toolkit-shared';

import { useLayoutEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigationType, useParams } from 'react-router';

import {
  SubnavAlertTriggerRoute,
  getAlertTriggerForRoute,
} from '../../components/MainHeader/HeaderNav/alertTriggerUtils.ts';
import { routeToStepMap } from '../../hooks/validation/consts.ts';
import {
  getActiveAlertsForRoute,
  useAppraisifyNavigation,
} from '../../hooks/validation/useAppraisifyNavigation.ts';
import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectAlerts } from '../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectIsInspectifyOrder } from '../../redux/features/selectors/orderSelectors.ts';
import {
  processAlertTrigger,
  processAlertsForRoute,
  retriggerAlertToasts,
} from '../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';

export const AppraisifyStepGuard = ({
  routeSegment,
  children,
}: React.PropsWithChildren<{ routeSegment: string }>) => {
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const { isStepEnabled, getLatestAllowedRoute, getEarliestRouteWithActiveAlertsBeforeTarget } =
    useAppraisifyNavigation();
  const dispatch = useAppDispatch();
  const alerts = useAlerts();
  const { orderId } = useParams();
  const { key: locationKey } = useLocation();
  const navigationType = useNavigationType();
  const establishedLocationKeyRef = useRef<string | null>(null);

  const step = routeToStepMap[routeSegment as RouteItem];
  const isValidationBlocked = Boolean(step && !isStepEnabled(step));
  const blockingRoute = getEarliestRouteWithActiveAlertsBeforeTarget(
    alerts,
    routeSegment as RouteItem,
  );
  const isBlockedByPriorPageAlerts = Boolean(
    isInspectifyOrder && navigationType !== 'POP' && blockingRoute,
  );
  const hasEstablishedRoute = establishedLocationKeyRef.current === locationKey;
  const shouldRedirectForPriorPageAlerts =
    isBlockedByPriorPageAlerts && blockingRoute && !hasEstablishedRoute;
  const alertTrigger = getAlertTriggerForRoute(routeSegment as SubnavAlertTriggerRoute);

  useLayoutEffect(() => {
    establishedLocationKeyRef.current = locationKey;
  }, [locationKey]);

  useLayoutEffect(() => {
    if (!isInspectifyOrder) {
      return;
    }
    if (shouldRedirectForPriorPageAlerts && blockingRoute) {
      const blockingRouteAlertTypes = getActiveAlertsForRoute(
        alerts,
        blockingRoute,
        alertsCommentary,
        true,
        explanationContext,
      ).map((alert) => alert.type);
      dispatch(
        processAlertsForRoute({
          route: blockingRoute,
          alertsPerSection: alerts,
          alertsCommentary,
          explanationContext,
        }),
      );
      if (blockingRouteAlertTypes.length > 0) {
        dispatch(retriggerAlertToasts(blockingRouteAlertTypes));
      }
      return;
    }
    if (!isValidationBlocked || !alertTrigger) {
      return;
    }
    dispatch(
      processAlertTrigger({
        trigger: alertTrigger,
        alertsPerSection: alerts,
        alertsCommentary,
        explanationContext,
      }),
    );
  }, [
    alertTrigger,
    alerts,
    alertsCommentary,
    explanationContext,
    dispatch,
    blockingRoute,
    isInspectifyOrder,
    isValidationBlocked,
    shouldRedirectForPriorPageAlerts,
  ]);

  if (!isInspectifyOrder) {
    return children;
  }

  if (shouldRedirectForPriorPageAlerts) {
    return <Navigate to={`/order/${expected(orderId)}/paired-cost/${blockingRoute}`} replace />;
  }

  if (!step || !isValidationBlocked) {
    return children;
  }

  return (
    <Navigate to={`/order/${expected(orderId)}/paired-cost/${getLatestAllowedRoute()}`} replace />
  );
};
